import React, { useEffect } from "react"
import Link from "./link"
import CallToAction from "./call-to-action"
import OnClickLink from "./link-onclick"
import JobsSubscription from "./job-subscription"
import { graphql } from "gatsby"
import AOS from "aos"
import stripParagraph from "../utils/strip-paragraph"

export const query = graphql`
  fragment HorizontalTeaserFields on DatoCmsTeaser {
    componentId: key
    id
    model {
      apiKey
    }
    title
    headlineNode {
      childMarkdownRemark {
        html
      }
    }
    label
    blurbNode {
      childMarkdownRemark {
        html
      }
    }
    image {
      url(imgixParams: { w: "800" })
      format
      alt
    }
    gifImage: image {
      url(imgixParams: { w: "800", fm: "gif" })
      alt
    }
    overlayImageAsset {
      url(imgixParams: { w: "800" })
      format
      alt
    }
    gifOverlayImageAsset: overlayImageAsset {
      url(imgixParams: { w: "800", fm: "gif" })
      alt
    }
    url
    urltext
    ctaType
    verticallyAlignText
    rightfull
    largeHeader
    hideHeadline
    extraLargeHeadingText
    extraLargeBlurbText
    useOverlayImage: overlayImage
    extraWide
    extraWideTextSection
    reverseImageTextOnMobile
    hideMobileImage
    imageFirstOnMobile
    imageSpecial
    data
    unit
    numberFineprint
    stickyImage
  }
`

export interface HorizontalTeaserProps
  extends Queries.HorizontalTeaserFieldsFragment {
  variant?: (
    | "rightfull"
    | "extrawide"
    | "minus"
    | "light"
    | "right"
    | "left"
    | "largeheader"
    | "largeheadermobile"
    | "nospacing"
    | "textbottom"
    | "lesspadding"
    | "leftalign"
    | "changeorder"
    | "linkbutton"
    | "greybackground"
    | "no-bottom-margin-mobile"
    | "imagespecial"
    | "largerimagemobile"
    | "stickyimage"
    | "no-image-mobile"
    | "extralargeheadingtext"
    | "extralargeblurbtext"
    | "extrawidetextsection"
    | "reverseimagetextonmobile"
    | "hidemobileimage"
  )[]
  extraClasses?: string
  dataaos?: string
  dataaosoffset?: string
  onClick?: () => void
}

const HorizontalTeaser = (props: HorizontalTeaserProps) => {
  const title =
    (props.headlineNode &&
      props.headlineNode.childMarkdownRemark &&
      props.headlineNode.childMarkdownRemark.html) ||
    props.title
  const label = props.label || ""
  const blurb =
    (props.blurbNode &&
      props.blurbNode &&
      props.blurbNode.childMarkdownRemark &&
      props.blurbNode.childMarkdownRemark.html) ||
    ""
  const url = props.url || ""
  const urltext = props.urltext || ""

  const imageIsGif = props.image && props.image.format === "gif"

  const overlayImageIsGif =
    props.overlayImageAsset && props.overlayImageAsset.format === "gif"

  const imgSrc = imageIsGif
    ? props.gifImage
      ? props.gifImage.url
      : ""
    : props.image
    ? props.image.url
    : ""

  const overlayImageSrc = overlayImageIsGif
    ? props.gifOverlayImageAsset
      ? props.gifOverlayImageAsset.url
      : ""
    : props.overlayImageAsset
    ? props.overlayImageAsset.url
    : ""

  const imgAlt = (props.image && props.image.alt) || ""
  const ctatype = props.ctaType || "text"
  const hideHeadline = props.hideHeadline
  const imageFirstOnMobile = props.imageFirstOnMobile
  const dataText = props.data
  const fineprint = props.numberFineprint
  const unit = props.unit

  useEffect(() => {
    AOS.init()
    AOS.refresh()
  })

  const classNames = ["c-horizontal-teaser"]

  if (props.rightfull) {
    classNames.push("-rightfull")
  }

  if (props.stickyImage) {
    classNames.push("-stickyimage")
  }

  if (props.largeHeader) {
    classNames.push("-largeheader")
  }

  if (props.extraLargeHeadingText) {
    classNames.push("-extralargeheadingtext")
  }

  if (props.extraLargeBlurbText) {
    classNames.push("-extralargeblurbtext")
  }

  if (props.extraWideTextSection) {
    classNames.push("-extrawidetextsection")
  }

  if (props.hideMobileImage) {
    classNames.push("-hidemobileimage")
  }

  if (props.extraWide) {
    classNames.push("-extrawide")
  }

  if (props.useOverlayImage) {
    classNames.push("-overlayimage")
  }

  if (props.overlayImageAsset) {
    classNames.push("-hasseperateoverlayimage")
  }

  if (props.reverseImageTextOnMobile) {
    classNames.push("-reverseimagetextonmobile")
  }

  if (props.imageFirstOnMobile) {
    classNames.push("-imagefirstonmobile")
  }

  if (props.imageSpecial) {
    classNames.push("-imagespecial")
  }

  if (props.variant) {
    props.variant.forEach(variant => {
      classNames.push(`-${variant}`)
    })
  }

  if (props.extraClasses) {
    classNames.push(props.extraClasses)
  }

  return (
    <section
      id={props.componentId}
      className={classNames.join(" ")}
      style={{
        backgroundImage: `url()`,
        backgroundPosition: "center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="o-wrapper u-flex u-flex-between u-flex-wrap o-row">
        <div
          className="c-horizontal-teaser__img-container"
          data-aos={props.dataaos}
          data-aos-offset={props.dataaosoffset}
          data-aos-delay="100"
        >
          <img className="c-horizontal-teaser__img" src={imgSrc} alt={imgAlt} />
        </div>
        {overlayImageSrc && (
          <div
            className="c-horizontal-teaser__img-container -overlay"
            data-aos={props.dataaos}
            data-aos-offset={props.dataaosoffset}
            data-aos-delay="100"
          >
            <img
              className="c-horizontal-teaser__img"
              src={overlayImageSrc}
              alt={imgAlt}
            />
          </div>
        )}
        <div
          className={`c-horizontal-teaser__content-container u-relative ${
            props.verticallyAlignText ? `-verticalAlign` : ""
          } `}
          data-aos={props.dataaos}
          data-aos-offset={props.dataaosoffset}
          data-aos-delay="100"
        >
          {label && (
            <span className="c-horizontal-teaser__label c-label -black">
              {label}
            </span>
          )}
          {!hideHeadline && !imageFirstOnMobile && (
            <h2
              className="c-horizontal-teaser__heading c-heading--b"
              dangerouslySetInnerHTML={{
                __html: stripParagraph(title),
              }}
            />
          )}
          <div className="c-horizontal-teaser__content-wrapper">
            <div className="c-horizontal-teaser__img-container-small">
              <img
                className="c-horizontal-teaser__img -small"
                src={imgSrc}
                alt={imgAlt}
              />
            </div>
            {!hideHeadline && imageFirstOnMobile && (
              <h2
                className="c-horizontal-teaser__heading c-heading--b"
                dangerouslySetInnerHTML={{
                  __html: stripParagraph(title),
                }}
              />
            )}
            <div className="c-horizontal-teaser__content-text">
              <div dangerouslySetInnerHTML={{ __html: blurb }} />
              {dataText && (
                <div>
                  <p className="c-horizontal-teaser__content-text__data">
                    {dataText}
                  </p>
                  {unit && (
                    <span className="c-horizontal-teaser__content-text__unit">
                      {" "}
                      {unit}
                    </span>
                  )}
                  {fineprint && (
                    <p className="c-horizontal-teaser__content-text__fineprint">
                      {fineprint}
                    </p>
                  )}
                </div>
              )}
              {props.onClick && urltext ? (
                <OnClickLink onClick={props.onClick}>{urltext}</OnClickLink>
              ) : (
                url &&
                urltext && (
                  <>
                    {ctatype === "text" && <Link url={url}>{urltext}</Link>}
                    {ctatype === "button" && (
                      <CallToAction url={url} color={"green"}>
                        {urltext}
                      </CallToAction>
                    )}
                  </>
                )
              )}
              {ctatype === "job" && <JobsSubscription showFilters={true} />}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
export default HorizontalTeaser
